import { formatDateTime } from '@dcp/shared/utils/formatters'
import type { CreditCardAvailabilityRow } from '@/lib/db-tenant'
import { RECONNECT_ELSEWHERE_HINT } from '@/components/ledgersync/ledgerSyncConnectedBanks.utils'
import { connectionNeedsAttention } from '@/lib/ledgersync-connection-attention'

/**
 * The warning for a card whose bank connection stopped refreshing, or null
 * when there is nothing to warn about.
 *
 * Manual cards (no connection) get none, nor do cards on a connection that
 * needs no attention (see `connectionNeedsAttention`). An unhealthy connection
 * keeps the card listed with its last known figures, so the warning names how
 * old those figures are — the vendor's `data_refreshed_at`,
 * not our own fetch time, which keeps advancing while the data is frozen.
 *
 * @param card - the card's connection status and vendor refresh time
 * @returns tooltip text, or null
 */
export function creditCardConnectionWarning(
  card: Pick<CreditCardAvailabilityRow, 'connection_status' | 'data_refreshed_at'>,
): string | null {
  if (!card.connection_status || !connectionNeedsAttention(card.connection_status)) return null
  const asOf = card.data_refreshed_at ? formatDateTime(card.data_refreshed_at) : 'unknown'
  return `Bank connection needs attention — figures as of ${asOf}. ${RECONNECT_ELSEWHERE_HINT}`
}
